/**
 * The tournament page's seven tabs (#2691, F3) and where every old `?tab=`
 * key went, so bookmarks and journeys keep working.
 */

export const TOURNAMENT_TABS = ["overview", "events", "entries", "draws", "schedule", "run", "results"] as const;
export type TournamentTab = (typeof TOURNAMENT_TABS)[number];
export const MORE_ITEMS = ["merchandise", "settings", "series"] as const;
export type MoreItem = (typeof MORE_ITEMS)[number];
export type TabKey = TournamentTab | MoreItem;

export const TAB_LABELS: Record<TabKey, string> = {
  overview: "Overview",
  events: "Events",
  entries: "Entries",
  draws: "Draws",
  schedule: "Schedule",
  run: "Run tournament",
  results: "Results",
  merchandise: "Merchandise",
  settings: "All settings",
  series: "Series",
};

/** Sub-sections shown as a second row of tabs. Names never repeat a top tab. */
export const SECTIONS: Partial<Record<TabKey, { key: string; label: string }[]>> = {
  entries: [
    { key: "registrations", label: "Registrations" },
    { key: "teams", label: "Teams" },
  ],
  draws: [
    { key: "setup", label: "Draw setup" },
    { key: "bracket", label: "Brackets" },
  ],
  run: [
    { key: "control", label: "Control center" },
    { key: "displays", label: "Displays" },
  ],
  results: [
    { key: "closeout", label: "Close out" },
    { key: "winners", label: "Winners" },
    { key: "reports", label: "Reports" },
    { key: "certificates", label: "Certificates" },
  ],
};

export type ResolvedTab = { tab: TabKey; section?: string };

export const LEGACY_TAB_TARGET: Record<string, ResolvedTab> = {
  courts: { tab: "overview", section: "basics" },
  staff: { tab: "overview", section: "officials" },
  coaches: { tab: "overview", section: "officials" },
  sponsors: { tab: "overview", section: "promo" },
  legal: { tab: "overview", section: "waiver" },
  displays: { tab: "run", section: "displays" },
  teams: { tab: "entries", section: "teams" },
  bracket: { tab: "draws", section: "bracket" },
  closeout: { tab: "results", section: "closeout" },
  winners: { tab: "results", section: "winners" },
  reports: { tab: "results", section: "reports" },
  certificates: { tab: "results", section: "certificates" },
  merchandise: { tab: "merchandise" },
};

/** "Where the old tabs went" (F3), in the frame's order. */
export const OLD_TAB_MAP: { old: string; now: string }[] = [
  { old: "Teams", now: "Entries › Teams (Events › entry type: Teams, per event, comes later)" },
  { old: "Staff and officials", now: "Overview › Referee and officials" },
  { old: "Courts", now: "Overview › Name, days and courts" },
  { old: "Sponsors", now: "Overview › Poster, sponsors and prizes" },
  { old: "Legal and waivers", now: "Overview › Waiver and tournament rules" },
  { old: "Venue displays", now: "Run tournament › Displays" },
  { old: "Merchandise", now: "More › Merchandise" },
  { old: "Certificates", now: "Results › Certificates" },
  { old: "Coaches", now: "Overview › Referee and officials" },
  { old: "Series", now: "More › Series" },
  { old: "Closeout", now: "Results › Close out" },
  { old: "Winners, reports", now: "Results › Winners and reports" },
];

export function resolveTournamentTab(param: string | null, section?: string | null): ResolvedTab {
  if (!param) return { tab: "overview" };
  if ((TOURNAMENT_TABS as readonly string[]).includes(param) || (MORE_ITEMS as readonly string[]).includes(param)) {
    return { tab: param as TabKey, section: section ?? undefined };
  }
  return LEGACY_TAB_TARGET[param] ?? { tab: "overview" };
}
